import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import AnimatedSection from "@/utils/AnimatedSection";

const steps = [
  { number: "01", title: "Login or Sign Up", description: "Create your free tots360 account in seconds — no clinic visit needed", accent: "#4ECDC4" },
  { number: "02", title: "Enter Child Details", description: "Add height, weight, age — or upload a hand X-ray for bone-age analysis", accent: "#A855F7" },
  { number: "03", title: "Instant AI Analysis", description: "Validated clinical models compute growth, bone-age and nutrition insights in seconds", accent: "#FF6B6B" },
  { number: "04", title: "Get Reports & Insights", description: "Download branded PDFs covering health status, growth trends and personalised nutrition", accent: "#FFE66D" }
];

const HowItWorks = ({ theme }) => {
  return (
    <section id="how-it-works-section" className="relative py-32 overflow-hidden" data-testid="how-it-works-section">
      <div className="absolute inset-0 bg-[#0F1C3F]" />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-20">
            <span className="inline-block px-5 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-6 bg-white/5 text-white/60 border border-white/10">
              The Process — 100% Online
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mb-6 text-white">
              Login & check your child&apos;s<br /> health status
            </h2>
            <p className="text-[#4ECDC4] text-2xl md:text-3xl font-display">
              Get nutrition insights in minutes
            </p>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.2}>
          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div
                key={i}
                className="group relative bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] rounded-2xl p-8 transition-all duration-500"
                data-testid={`step-${i}`}
              >
                <div className="text-[80px] font-display font-bold leading-none mb-4 transition-colors duration-300" style={{ color: step.accent, opacity: 0.3 }}>
                  {step.number}
                </div>
                <div className="w-12 h-1 rounded-full mb-5 transition-all duration-300 group-hover:w-20" style={{ backgroundColor: step.accent }} />
                <h3 className="font-display text-xl mb-3 text-white">{step.title}</h3>
                <p className="text-sm leading-relaxed text-white/50">{step.description}</p>
                {i < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10 text-white/20">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.4}>
          <div className="text-center mt-16">
            <p className="text-lg mb-6 text-white/60">Ready to try it now?</p>
            <Link
              to="/login"
              data-testid="how-it-works-cta-login"
              className="inline-flex items-center gap-2 btn-coral px-8 py-4 rounded-full font-semibold text-lg"
            >
              Login & Get Started
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default HowItWorks;
